/**
 * Persistent Storage Manager
 * Requests browser to never auto-delete data
 * Protects IndexedDB from OS purging
 *
 * @version 1.0.0
 * @date 2025-11-13
 */

class PersistentStorageManager {
  constructor() {
    this.PERSISTENCE_KEY = 'storage_persistence_status';
  }

  /**
   * Request persistent storage from browser
   * Browser will never auto-delete data if granted
   * @returns {Promise<Object>} Result with granted status and reason
   */
  async requestPersistence() {
    // Check if Storage API is available
    if (!navigator.storage || !navigator.storage.persist) {
      console.warn('Persistent Storage API not supported');
      return {
        granted: false,
        reason: 'not_supported',
        message: 'Tu navegador no soporta almacenamiento persistente'
      };
    }

    try {
      // Check if already granted
      const isPersisted = await navigator.storage.persisted();
      if (isPersisted) {
        this._saveStatus(true);
        return {
          granted: true,
          alreadyGranted: true,
          message: 'Almacenamiento ya está protegido'
        };
      }

      // Request persistence (browser may show prompt)
      const granted = await navigator.storage.persist();
      this._saveStatus(granted);

      return {
        granted,
        alreadyGranted: false,
        reason: granted ? 'user_granted' : 'user_denied',
        message: granted
          ? 'Almacenamiento protegido exitosamente'
          : 'El navegador no otorgó protección. Haz backups regulares.'
      };

    } catch (error) {
      console.error('Error requesting persistent storage:', error);
      return {
        granted: false,
        reason: 'error',
        message: 'Error al solicitar protección: ' + error.message
      };
    }
  }

  /**
   * Check if storage is currently persisted
   * @returns {Promise<boolean>} True if persisted
   */
  async checkPersistence() {
    if (!navigator.storage || !navigator.storage.persisted) {
      return false;
    }

    try {
      return await navigator.storage.persisted();
    } catch (error) {
      console.error('Error checking persistence:', error);
      return false;
    }
  }

  /**
   * Get storage quota and usage information
   * @returns {Promise<Object>} Storage statistics
   */
  async getStorageEstimate() {
    if (!navigator.storage || !navigator.storage.estimate) {
      return {
        usage: 0,
        quota: 0,
        percentage: 0,
        available: true
      };
    }

    try {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentage = quota > 0 ? (usage / quota) * 100 : 0;

      return {
        usage,
        quota,
        percentage,
        usageGB: (usage / (1024 ** 3)).toFixed(2),
        quotaGB: (quota / (1024 ** 3)).toFixed(2),
        usageMB: (usage / (1024 ** 2)).toFixed(2),
        quotaMB: (quota / (1024 ** 2)).toFixed(2),
        available: percentage < 90,
        persisted: await this.checkPersistence()
      };
    } catch (error) {
      console.error('Error getting storage estimate:', error);
      return {
        usage: 0,
        quota: 0,
        percentage: 0,
        usageGB: '0.00',
        quotaGB: '0.00',
        available: true,
        error: error.message
      };
    }
  }

  /**
   * Get detailed storage info for user display
   * @returns {Promise<Object>} User-friendly storage info
   */
  async getStorageInfo() {
    const estimate = await this.getStorageEstimate();
    const isPersisted = await this.checkPersistence();

    return {
      isPersisted,
      isSupported: !!(navigator.storage && navigator.storage.persist),
      usage: {
        bytes: estimate.usage,
        mb: estimate.usageMB,
        gb: estimate.usageGB,
        formatted: this._formatBytes(estimate.usage)
      },
      quota: {
        bytes: estimate.quota,
        mb: estimate.quotaMB,
        gb: estimate.quotaGB,
        formatted: this._formatBytes(estimate.quota)
      },
      percentage: estimate.percentage.toFixed(1),
      available: estimate.available,
      status: this._getStorageStatus(estimate.percentage, isPersisted)
    };
  }

  /**
   * Format bytes to human-readable string
   * @private
   */
  _formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get storage status message
   * @private
   */
  _getStorageStatus(percentage, isPersisted) {
    if (percentage > 90) {
      return {
        level: 'critical',
        message: 'Almacenamiento casi lleno. Exporta y elimina viajes antiguos.',
        icon: '🔴'
      };
    }

    if (percentage > 70) {
      return {
        level: 'warning',
        message: 'Almacenamiento alto. Considera hacer backup y liberar espacio.',
        icon: '🟡'
      };
    }

    if (!isPersisted) {
      return {
        level: 'info',
        message: 'Sin protección automática. Haz backups regulares.',
        icon: '🔵'
      };
    }

    return {
      level: 'good',
      message: 'Almacenamiento protegido y con espacio disponible.',
      icon: '🟢'
    };
  }

  /**
   * Save persistence status to localStorage
   * @private
   */
  _saveStatus(granted) {
    try {
      localStorage.setItem(this.PERSISTENCE_KEY, JSON.stringify({
        granted,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error saving persistence status:', error);
    }
  }

  /**
   * Check if user was previously warned about persistence
   * @returns {boolean}
   */
  wasUserWarned() {
    try {
      const status = localStorage.getItem(this.PERSISTENCE_KEY);
      return !!status;
    } catch (error) {
      return false;
    }
  }
}

// Export singleton instance
const persistentStorage = new PersistentStorageManager();
export default persistentStorage;
